import { Header, Modal, Progress } from "semantic-ui-react";

import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../ui/components/Buttons/Button";
import FileDownloadTable from "../../../ui/components/FileDownload/FileDownloadTable";

export default function FileDownloadModal({ filesOrFolder, downloadFn, zipDownloadFn, onClose, infoText }) {
	const { t } = useTranslation();

	const [progressMap, setProgressMap] = useState({});
	const [downloading, setDownloading] = useState(false);
	const [error, setError] = useState(null);
	const abortRef = useRef(null);

	const files = useMemo(() => {
		if (Array.isArray(filesOrFolder)) {
			return filesOrFolder.map((f) => ({ name: f.name }));
		}
		return Object.keys(progressMap).map((name) => ({ name }));
	}, [filesOrFolder, progressMap]);

	const totalProgress =
		files.length > 0
			? Math.round((Object.values(progressMap).reduce((sum, p) => sum + p, 0) / files.length) * 100)
			: 0;

	const handleDownload = async (fn) => {
		setDownloading(true);
		setError(null);

		const controller = new AbortController();
		abortRef.current = controller;

		try {
			await fn(filesOrFolder, (progress) => setProgressMap(progress), controller.signal);
			onClose();
		} catch (err) {
			if (err.name === "AbortError") {
				console.info("Download aborted");
			} else {
				console.error(err);
				setError(err.message);
			}
			setDownloading(false);
		}
	};

	const handleCancel = () => {
		if (downloading && abortRef.current) {
			abortRef.current.abort();
		}
		onClose();
	};

	return (
		<Modal open size="tiny" onClose={onClose} closeOnDimmerClick={!downloading}>
			<Modal.Content scrolling>
				<Header>{t("download", "Download")}</Header>
				{infoText && <i>{infoText}</i>}

				{files.length > 0 && (
					<>
						<Progress percent={totalProgress} progress indicating />
						<FileDownloadTable files={files} progress={progressMap} style={{ marginTop: 10 }} />
					</>
				)}

				{error && (
					<Header as="h5" color="red">
						{t("error", "Error")}: {error}
					</Header>
				)}
			</Modal.Content>

			<Modal.Actions>
				<Button onClick={handleCancel}>{t("cancel", "Cancel")}</Button>

				{zipDownloadFn && (
					<Button disabled={downloading} onClick={() => handleDownload(zipDownloadFn)}>
						{t("downloadZip", "Download zip")}
					</Button>
				)}

				<Button primary disabled={downloading} onClick={() => handleDownload(downloadFn)}>
					{t("download", "Download")}
				</Button>
			</Modal.Actions>
		</Modal>
	);
}
